/**
 * 引用来源列表（T12）。
 *
 * 在助手消息气泡下方提供可折叠的「引用来源」：每条含标题 / URL / 摘要，点击在浏览器
 * **新标签页**打开（前端 `target="_blank"` 即可，不需要跨端能力）。
 *
 * 结构与对话页其它部件一致：所有可见节点都用 `useComponentMeta()` 登记 390x844 锚点；
 * 子项锚点相对宿主气泡布局换算，父级气泡布局由调用方传入。
 */

import { motion } from 'framer-motion';
import { useState } from 'react';
import type { ReactElement } from 'react';

import type { SearchResult } from '../../core/search';
import { useMotionPresets } from '../../design/motion';
import type { MotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import styles from './search.module.css';

export interface CitationSourcesProps {
  /** 宿主消息气泡的登记 id 前缀，如 `chat.message.assistant.<id>`。 */
  baseId: string;
  /** 宿主气泡在 390x844 画布上的声明布局（用于换算子项锚点）。 */
  layout: BoxLayout;
  results: readonly SearchResult[];
}

/** 折叠标题行高度（dp）。 */
const TOGGLE_HEIGHT = 24;
/** 单条引用高度（dp）。 */
const ITEM_HEIGHT = 52;
/** 条目间距（dp）。 */
const ITEM_GAP = 8;
/** 引用区相对气泡左侧的内边距（dp）。 */
const INNER_PADDING = 12;

interface CitationLayouts {
  list: BoxLayout;
  toggle: BoxLayout;
  items: BoxLayout[];
}

function citationLayouts(layout: BoxLayout, count: number): CitationLayouts {
  const x = layout.x + INNER_PADDING;
  const y = layout.y + 44;
  const width = layout.width - INNER_PADDING * 2;
  const list: BoxLayout = { x, y, width, height: TOGGLE_HEIGHT + count * (ITEM_HEIGHT + ITEM_GAP) };
  const toggle: BoxLayout = { x, y, width, height: TOGGLE_HEIGHT };
  const items = Array.from({ length: count }, (_, index) => ({
    x,
    y: y + TOGGLE_HEIGHT + ITEM_GAP + index * (ITEM_HEIGHT + ITEM_GAP),
    width,
    height: ITEM_HEIGHT,
  }));
  return { list, toggle, items };
}

/** 用占位符兜底空标题 / 空链接，避免出现「[]」式的空条目（不含 any）。 */
function safeTitle(result: SearchResult): string {
  const title = result.title.trim();
  return title === '' ? '(无标题)' : title;
}

function safeUrl(result: SearchResult): string {
  const url = result.url.trim();
  return url === '' ? '(无链接)' : url;
}

interface CitationItemProps {
  meta: ComponentMetaInput;
  result: SearchResult;
  presets: MotionPresets;
}

function CitationItem({ meta, result, presets }: CitationItemProps): ReactElement {
  const ref = useComponentMeta<HTMLLIElement>(buildComponentMeta(meta, 'list'));
  const url = safeUrl(result);
  const snippet = result.snippet.trim();
  const openInNewTab = url === '(无链接)' ? undefined : url;

  return (
    <motion.li
      ref={ref}
      className={styles.citationItem}
      data-ui-id={meta.id}
      data-ui-role="list"
      variants={presets.listStagger.item}
    >
      <a
        className={styles.citationLink}
        href={openInNewTab ?? '#'}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={openInNewTab === undefined}
        onClick={(event) => {
          if (openInNewTab === undefined) event.preventDefault();
        }}
      >
        <span className={styles.citationTitle}>{safeTitle(result)}</span>
        <span className={styles.citationUrl}>{url}</span>
        {snippet === '' ? null : <span className={styles.citationSnippet}>{snippet}</span>}
      </a>
    </motion.li>
  );
}

/** 引用来源折叠区；`results` 为空时不渲染任何节点。 */
export function CitationSources({
  baseId,
  layout,
  results,
}: CitationSourcesProps): ReactElement | null {
  const presets = useMotionPresets();
  const [expanded, setExpanded] = useState(false);

  const layouts = citationLayouts(layout, results.length);
  const listMeta: ComponentMetaInput = {
    id: `${baseId}.citations`,
    page: 'chat',
    role: 'container',
    label: '引用来源列表',
    layout: layouts.list,
    zIndex: 5,
    props: ['expanded'],
    notes: '助手消息下方可折叠引用来源（标题 / URL / 摘要）；安卓对应可折叠 CardView',
  };
  const toggleMeta: ComponentMetaInput = {
    id: `${baseId}.citations.toggle`,
    page: 'chat',
    role: 'button',
    label: '展开/收起引用来源',
    layout: layouts.toggle,
    zIndex: 6,
    props: ['expanded'],
    notes: '点击展开 / 收起引用来源；安卓对应 MaterialButton（带展开箭头）',
  };
  const listRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(listMeta, 'container'));
  const toggleRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(toggleMeta, 'button'));

  if (results.length === 0) return null;

  return (
    <div
      ref={listRef}
      className={styles.citations}
      data-ui-id={listMeta.id}
      data-ui-role="container"
      data-expanded={expanded ? 'true' : 'false'}
    >
      <motion.button
        ref={toggleRef}
        type="button"
        className={styles.citationToggle}
        data-ui-id={toggleMeta.id}
        data-ui-role="button"
        aria-expanded={expanded}
        aria-label={expanded ? '收起引用来源' : '展开引用来源'}
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className={styles.citationCaret} aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
        <span>{`引用来源 (${results.length})`}</span>
      </motion.button>

      {expanded ? (
        <motion.ul
          className={styles.citationList}
          variants={presets.listStagger.container}
          initial="hidden"
          animate="visible"
        >
          {results.map((result, index) => {
            const itemLayout = layouts.items[index];
            if (itemLayout === undefined) return null;
            const itemMeta: ComponentMetaInput = {
              id: `${baseId}.citations.item.${index}`,
              page: 'chat',
              role: 'list',
              label: `引用条目 ${index + 1}`,
              layout: itemLayout,
              zIndex: 5,
              props: ['title', 'url', 'snippet'],
              notes: '单条引用来源，点击在新标签页打开 URL；安卓对应 RecyclerView item',
            };
            return (
              <CitationItem
                key={`${baseId}.citations.item.${index}`}
                meta={itemMeta}
                result={result}
                presets={presets}
              />
            );
          })}
        </motion.ul>
      ) : null}
    </div>
  );
}
